import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getApplicationTenants, selectSite, selectTenant } from '../services/coreApi';
import { authStorage, decodeTokenClaims } from '../services/authStorage';
import { useAuth } from '../hooks';
import type { AuthTokenResponse, CoreTenant } from '../types';
import { findTenant, firstEnabledSite } from '../utils/sites';
import { accountTypeFromRoles, rolesFromClaims } from '../utils/userDisplay';
import { homePath } from '../utils/verification';

interface TenantContextValue {
  tenants: CoreTenant[];
  currentTenantId: string | null;
  currentTenant: CoreTenant | null;
  loading: boolean;
  switching: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  error: string | null;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function accountTypeFromToken(accessToken: string) {
  return accountTypeFromRoles(rolesFromClaims(decodeTokenClaims(accessToken)));
}

async function tokenForTenant(tenantId: string, tenants: CoreTenant[]): Promise<AuthTokenResponse> {
  const data = await selectTenant(tenantId);
  if (accountTypeFromToken(data.accessToken)) return data;

  // Core only puts roles on the JWT when a site is bound. Login picks the first
  // site; select-tenant does not if the tenant has more than one site — which
  // left managers looking like employees after a switch.
  const site = firstEnabledSite(findTenant(tenants, tenantId));
  const siteId = site?.tenantSiteId || site?.id;
  if (!siteId) return data;

  return selectSite(siteId, data.accessToken);
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tenants, setTenants] = useState<CoreTenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentTenantId = useMemo(() => {
    const claims = authStorage.getTokenClaims();
    return typeof claims.tenantId === 'string' ? claims.tenantId : null;
  }, [isAuthenticated, tenants]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTenants([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getApplicationTenants()
      .then(list => {
        if (!cancelled) setTenants(list);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load tenants');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const switchTenant = useCallback(async (tenantId: string) => {
    if (tenantId === currentTenantId) return;
    setSwitching(true);
    setError(null);
    try {
      const data = await tokenForTenant(tenantId, tenants);
      const nextAccount = accountTypeFromToken(data.accessToken);
      if (!nextAccount) {
        throw new Error('No Passport role on switched token');
      }
      authStorage.setSession({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        refreshToken: data.refreshToken,
        user: data.user ?? authStorage.getCachedUser()!,
      });
      window.location.assign(homePath(nextAccount));
    } catch {
      setError('Could not switch tenant');
      setSwitching(false);
    }
  }, [currentTenantId, tenants]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenants,
      currentTenantId,
      currentTenant: findTenant(tenants, currentTenantId) ?? null,
      loading,
      switching,
      switchTenant,
      error,
    }),
    [tenants, currentTenantId, loading, switching, switchTenant, error],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenants = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenants must be used within TenantProvider');
  return ctx;
};
